import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { Metamodel } from '../base/model';
import { Model } from 'app/model/backendmodel';
import { IModel, IAttribute } from '../base/annotation';
import { LinkedService } from '../linkedservice/linkedservice';

import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { validateAKVLinkedserviceReference } from 'app/shared/utils/linkedservice-validation-util';
import { format } from 'app/shared/utils/string.util';
import { AuthenticationReferenceType, AuthenticationType, IViolation, shellStringResources, Credential } from 'shell-models-lib';
import { DatabricksParameter } from 'app/model/pipeline/databricks-parameter';

export interface IDockerContainerSettings {
    containerUrl: string;
    useBasicAuth: boolean;
    username?: string;
    password?: string;
}

@Metamodel.model({
    name: 'AzureDatabricks',
    display: [
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureDataBricks_DisplayText),
        format(stringResources.ModelName_LinkedServicesTemplate, stringResources.AzureDataBricks_DisplayText)
    ]
})
export class AzureDataBricksLinkedService extends LinkedService {

    public static readonly MODEL: IModel;
    public static readonly DOMAIN: IAttribute;
    public static readonly NODE_TYPE: IAttribute;
    public static readonly NUM_WORKER: IAttribute;
    public static readonly SPARK_CONF: IAttribute;
    public static readonly ENV_VARIABLES: IAttribute;
    public static readonly CUSTOM_TAGS: IAttribute;
    public static readonly VERSION: IAttribute;
    public static readonly DESTINATION_TYPE: IAttribute;
    public static readonly DBFS_DESTINATION: IAttribute;
    public static readonly INIT_SCRIPTS: IAttribute;
    public static readonly ENABLE_ELASTIC_DISK: IAttribute;
    public static readonly EXISTING_CLUSTER_ID: IAttribute;
    public static readonly DRIVER_NODE_TYPE: IAttribute;
    public static readonly INSTANCE_POOL_ID: IAttribute;
    public static readonly WORKSPACE_RESOURCE_ID: IAttribute;
    public static readonly ADDITIONAL_CLUSTER_ATTRIBUTES: IAttribute;
    public static readonly CLUSTER_OPTION: IAttribute;
    public static readonly DOCKER_CONFIGURATION: IAttribute;
    public static readonly POLICY_ID: IAttribute;
    public static readonly DATA_SECURITY_MODE: IAttribute;

    public useClusterMode: 'new' | 'exist' | 'pool' | 'serverless' = 'new';

    @Metamodel.attribute({
        display: shellStringResources.ModelName_AuthenticationType
    })
    public authenticationType: AuthenticationType = AuthenticationType.AccessToken;

    @Metamodel.attribute({
        display: stringResources.Authentication_Reference_Method_Label
    })
    public authenticationReferenceType: AuthenticationReferenceType = AuthenticationReferenceType.Inline;

    @Metamodel.attribute({
        display: shellStringResources.Credential,
        reference: true,
        constraint: {
            validator: AzureDataBricksLinkedService.validateCredential
        }
    })
    public credential?: Credential;

    @Metamodel.attribute({
        display: stringResources.ModelName_Domain,
        templatable: true,
        association: 'DOMAIN'
    })
    public domain?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_AccessToken,
        constraint: {
            validator: validateAKVLinkedserviceReference
        }
    })
    public accessToken?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.ModelName_WorkspaceResourceId,
        templatable: true,
        association: 'WORKSPACE_RESOURCE_ID'
    })
    public workspaceResourceId?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ExistingClusterId,
        templatable: true,
        association: 'EXISTING_CLUSTER_ID'
    })
    public existingClusterId?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_InstancePoolId,
        templatable: true,
        association: 'INSTANCE_POOL_ID'
    })
    public instancePoolId?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterVersion,
        templatable: true,
        association: 'VERSION'
    })
    public newClusterVersion?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterNumOfWorker,
        templatable: true,
        association: 'NUM_WORKER'
    })
    public newClusterNumOfWorker?: string = '1';

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterNodeType,
        templatable: true,
        association: 'NODE_TYPE'
    })
    public newClusterNodeType?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterSparkConf,
        templatable: true,
        association: 'SPARK_CONF'
    })
    public newClusterSparkConf?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterSparkEnvVars,
        templatable: true,
        association: 'ENV_VARIABLES'
    })
    public newClusterSparkEnvVars?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterCustomTags,
        templatable: true,
        association: 'CUSTOM_TAGS'
    })
    public newClusterCustomTags?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterInitScripts,
        templatable: true,
        association: 'INIT_SCRIPTS'
    })
    public newClusterInitScripts?: DatabricksParameter[] | ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterDriverNodeType,
        templatable: true,
        association: 'DRIVER_NODE_TYPE'
    })
    public newClusterDriverNodeType?: string;

    @Metamodel.attribute({
        display: stringResources.DestinationLabel,
        templatable: true,
        association: 'DESTINATION_TYPE'
    })
    public newClusterLogDestinationType?: string = 'Dbfs';

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterLogDestination,
        templatable: true,
        association: 'DBFS_DESTINATION'
    })
    public newClusterLogDestination?: string;

    @Metamodel.attribute({
        display: stringResources.DatabricksClusterAdditionalSettings,
        templatable: false,
        association: 'ADDITIONAL_CLUSTER_ATTRIBUTES'
    })
    public newClusterAttributes: Model.INewClusterAttributes = {
        firstOnDemand: 1,
        availability: 'OnDemand',
        spotBidMaxPrice: -1
    };

    @Metamodel.attribute({
        display: stringResources.WorkerOptions,
        templatable: false,
        association: 'CLUSTER_OPTION'
    })
    public clusterOption: string = 'Fixed';

    @Metamodel.attribute({
        display: stringResources.Databricks_DockerContainerLabel,
        templatable: false,
        association: 'DOCKER_CONFIGURATION'
    })
    public dockerConfiguration: IDockerContainerSettings = undefined;

    @Metamodel.attribute({
        display: stringResources.DataBricks_Cluster_Policy,
        templatable: false,
        association: 'POLICY_ID'
    })
    public policyId: string = undefined;

    @Metamodel.attribute({
        display: stringResources.DataBricks_Cluster_Unity_Catalog_Access_Mode,
        templatable: false,
        association: 'DATA_SECURITY_MODE'
    })
    public dataSecurityMode: string;

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    private static validateCredential(linkedservice: AzureDataBricksLinkedService, referencedCredential: Credential): IViolation[] | undefined {
        if ((linkedservice.authenticationType === AuthenticationType.UserAssignedManagedIdentity ||
                (linkedservice.authenticationType === AuthenticationType.ManagedServiceIdentity && linkedservice.authenticationReferenceType === AuthenticationReferenceType.Credential)) &&
            !referencedCredential) {
            return [{
                context: linkedservice,
                message: () => shellStringResources.Error_InvalidCredentialReferenced
            }];
        }
        return undefined;
    }
}
